import React, { useState } from 'react';
import { Dialog, DialogTitle, DialogContent, Alert } from '@mui/material';
import { supabase } from '../services/supabaseClient';
import { updateLancamento, deleteAnexosStorage, Lancamento } from '../services/api';
import { LancamentoBaseForm, LancamentoFormData } from './LancamentoBaseForm';

interface LancamentoEditModalProps {
    lancamento: Lancamento | null;
    initialStep?: number;
    open: boolean;
    onClose: () => void;
    onLancamentoUpdated: () => void;
}

const LancamentoEditModal: React.FC<LancamentoEditModalProps> = ({ lancamento, initialStep, open, onClose, onLancamentoUpdated }) => {
    const [isLoadingSubmit, setIsLoadingSubmit] = useState(false);
    const [error, setError] = useState('');

    const handleSubmit = async (data: LancamentoFormData, novosAnexos: File[], anexosDeletadosIds: number[]) => {
        if (!lancamento) return;
        setIsLoadingSubmit(true);
        setError('');

        try {
            const { data: { user } } = await supabase.auth.getUser();
            if (!user) throw new Error("Usuário não autenticado.");

            // Upload new attachments
            const uploadedAnexos = [];
            for (const file of novosAnexos) {
                const sanitizedFileName = file.name.normalize('NFD').replace(/[\u0300-\u036f]/g, '').replace(/[^a-zA-Z0-9.-]/g, '_');
                const filePath = `${user.id}/${Date.now()}-${sanitizedFileName}`;
                const { error: uploadError } = await supabase.storage.from('anexos').upload(filePath, file);
                if (uploadError) throw uploadError;
                uploadedAnexos.push({ caminho_arquivo: filePath, nome_original: file.name });
            }

            const lancamentoData = {
                ...data,
                desconto_valor: (data.tipo_desconto === 'total' && data.desconto_valor) ? (parseFloat(String(data.desconto_valor)) || 0) : 0,
                desconto_percentual: (data.tipo_desconto === 'total' && data.desconto_percentual) ? (parseFloat(String(data.desconto_percentual)) || 0) : 0,
            };
            delete (lancamentoData as any).items;
            delete (lancamentoData as any).anexos;

            const itemsData = data.items.map(item => ({
                ...item,
                quantidade: parseFloat(String(item.quantidade)) || 0,
                valor_unitario: parseFloat(String(item.valor_unitario)) || 0,
                desconto_valor: parseFloat(String(item.desconto_valor)) || 0,
                desconto_percentual: (item as any).desconto_percentual || 0,
            }));

            const anexosParaDeletarPaths = lancamento.anexos
                ?.filter((a: any) => anexosDeletadosIds.includes(a.id))
                .map((a: any) => a.caminho_arquivo) || [];
                
            if (anexosParaDeletarPaths.length > 0) {
                await deleteAnexosStorage(anexosParaDeletarPaths);
            }

            await updateLancamento(lancamento.id, lancamentoData as any, itemsData, uploadedAnexos, anexosDeletadosIds);

            onLancamentoUpdated();
            onClose();
        } catch (err: any) {
            console.error("Erro ao atualizar lançamento:", err);
            setError(err.message || "Erro ao atualizar lançamento.");
            throw err;
        } finally {
            setIsLoadingSubmit(false);
        }
    };

    // Prepara os dados iniciais adaptados para o formato do LancamentoBaseForm
    const initialData: Partial<LancamentoFormData> | undefined = lancamento ? {
        ...lancamento,
        obra_id: String(lancamento.obra_id),
        fornecedor_id: String(lancamento.fornecedor_id),
        investidor_id: String(lancamento.investidor_id),
        items: (lancamento.items || []).map(item => ({
            ...item,
            fase_obra_id: String(item.fase_obra_id),
            categoria_id: String(item.categoria_id),
            quantidade: item.quantidade || 0,
            valor_unitario: item.valor_unitario === 0 ? '' : item.valor_unitario,
            desconto_valor: item.desconto_valor === 0 ? '' : item.desconto_valor,
        })) as any
    } : undefined;

    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
            <DialogTitle>Editar Lançamento</DialogTitle>
            <DialogContent>
                {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
                {lancamento && (
                    <LancamentoBaseForm
                        initialData={initialData}
                        initialStep={initialStep}
                        isEditMode={true}
                        onSubmit={handleSubmit}
                        onCancel={onClose}
                        isLoadingSubmit={isLoadingSubmit}
                    />
                )}
            </DialogContent>
        </Dialog>
    );
};

export default LancamentoEditModal;
